import React, { useEffect, useState, useRef } from 'react';
import {useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import PropertyService from '../../services/PropertyService';
import BuilderService from '../../services/BuilderService';
import PropertyDocsMediaUpload, { docsMediaFromProperty } from './PropertyDocsMediaUpload';
import { INVENTORY_SOURCES } from '../../constants/propertyConstants';

function EditProperty() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    propertyName: "",
    ownerName: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    price:"",
    size:"",
    description: "",
    propertyType:"",
    yearBuilt:"",
    bedrooms:"",
    bathrooms:"",
    isActive: false,
    id: id,
    clientId: "",
  });
  const [formError, setFormError] = useState({});
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState([]);
  const [builders, setBuilders] = useState([]);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const property = await PropertyService.getPropertyById(id);
        setForm({
          ...property,
          inventorySource: property.inventorySource || 'Own',
          builderId: property.builderId != null ? String(property.builderId) : '',
          channelCommissionPercent:
            property.channelCommissionPercent != null && property.channelCommissionPercent !== ''
              ? String(property.channelCommissionPercent)
              : '',
          channelAgreementNotes: property.channelAgreementNotes || '',
        });
        try {
          const bl = await BuilderService.getByClientId(property.clientId);
          setBuilders(Array.isArray(bl) ? bl : []);
        } catch {
          setBuilders([]);
        }
        const { documents: d, images: im } = docsMediaFromProperty(property);
        setDocuments(Array.isArray(d) ? d : []);
        setImages(Array.isArray(im) ? im : []);
      } catch (error) {
        console.error('Error fetching property details:', error);
      }
    };
    fetchPropertyData();
  }, [id]);

  const changeHandler = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(prevForm => ({ ...prevForm, [name]: checked }));
    } else if (files) {
      setForm(prevForm => ({ ...prevForm, [name]: files[0] }));
    } else {
      setForm(prevForm => ({ ...prevForm, [name]: value }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!String(form.propertyName || '').trim()) errors.propertyName = 'Property name is required';
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const updateContractor = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    if ((form.inventorySource || 'Own') === 'ChannelPartner' && !form.builderId) {
      if (toast.current) {
        toast.current.show({
          severity: 'warn',
          summary: 'Builder required',
          detail: 'Channel partner inventory needs a linked builder.',
          life: 4000,
        });
      }
      return;
    }

    const formDataToSend = new FormData();

    Object.keys(form).forEach(key => {
      if (key === 'userImage' && form[key] instanceof File) {
        formDataToSend.append(key, form[key]);
      } else if (form[key] !== undefined && form[key] !== null && typeof form[key] !== 'object') {
        formDataToSend.append(key, form[key]);
      }
    });

    formDataToSend.set('documentsJson', JSON.stringify(documents));
    formDataToSend.set('imagesJson', JSON.stringify(images));

    const src = form.inventorySource || 'Own';
    formDataToSend.set('inventorySource', src);
    formDataToSend.set('builderId', src === 'ChannelPartner' && form.builderId ? String(form.builderId) : '');
    formDataToSend.set(
      'channelCommissionPercent',
      src === 'ChannelPartner' && form.channelCommissionPercent !== '' ? String(form.channelCommissionPercent) : ''
    );
    formDataToSend.set('channelAgreementNotes', src === 'ChannelPartner' ? (form.channelAgreementNotes || '') : '');

    try {
      await PropertyService.updateProperty(id, formDataToSend);

      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Contractor updated successfully',
          className: 'custom-toast-success',
          life: 3000
        });
      }
      setTimeout(() => {
        navigate(-1); // Navigate after toast shows
      }, 1000);
    } catch (error) {
      console.error("Error updating Contractor:", error);
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update user',
          className: 'custom-toast-delete',
          life: 3000
        });

      }
    }
  };
  
  const typeColors = {
    '1BHK': '#5e72e4', '2BHK': '#2dce89', '3BHK': '#11cdef',
    '4BHK': '#fb6340', '5BHK': '#f5365c', 'Plot': '#344767', 'Kothi': '#8965e0',
  };

  const formatPrice = (price) => {
    if (!price) return null;
    const num = Number(price);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const lbl = {
    display: 'block', fontSize: '12px', fontWeight: '600',
    color: '#8392ab', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };
  const inp = (err) => ({
    width: '100%', padding: '10px 14px',
    border: `1.5px solid ${err ? '#f5365c' : '#e9ecef'}`,
    borderRadius: '10px', fontSize: '14px', color: '#344767',
    background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
  });
  const errMsg = { fontSize: '11px', color: '#f5365c', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' };

  const SecHead = ({ color, icon, title }) => (
    <div style={{ paddingBottom: '10px', borderBottom: '1.5px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
      <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '13px' }}>
        <i className={`fas ${icon}`} />
      </div>
      <p style={{ fontSize: '13px', fontWeight: '700', color: '#344767', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>{title}</p>
    </div>
  );

  const Field = ({ label, name, type = 'text', placeholder }) => (
    <div className="col-md-4">
      <label style={lbl} htmlFor={name}>{label}</label>
      <input
        type={type} id={name} name={name}
        className="ep-input"
        style={inp(!!formError[name])}
        value={form[name] || ''} onChange={changeHandler}
        placeholder={placeholder || label}
      />
      {formError[name] && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError[name]}</p>}
    </div>
  );

  const typeColor = typeColors[form.propertyType] || '#344767';
  const formattedPrice = formatPrice(form.price);

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f4f6fb' }}>
      <Toast ref={toast} />
      <style>{`
        .ep-input:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; }
        .ep-select:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; outline: none; }
        .ep-submit:hover { opacity: 0.88; transform: translateY(-1px); }
        .ep-ghost:hover { background: #f0f2f5 !important; }
      `}</style>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '20px', boxShadow: '0 4px 12px rgba(52,71,103,0.3)',
          }}>
            <i className="fas fa-edit" />
          </div>
          <div>
            <p style={{ fontSize: '22px', fontWeight: '700', color: '#344767', margin: 0, lineHeight: 1.2 }}>Edit Property</p>
            <p style={{ fontSize: '13px', color: '#8392ab', margin: 0, marginTop: '2px' }}>Property ID #{id}</p>
          </div>
        </div>
        <button
          type="button"
          className="ep-ghost"
          onClick={() => navigate(-1)}
          style={{
            background: '#fff', border: '1.5px solid #e9ecef', borderRadius: '10px',
            padding: '9px 20px', fontSize: '13px', fontWeight: '600', color: '#525f7f',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', transition: 'all 0.2s',
          }}
        >
          <i className="fas fa-arrow-left" />Back
        </button>
      </div>

      {/* Property Identity Banner */}
      {form.propertyName && (
        <div style={{
          background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
          borderRadius: '16px', padding: '20px 24px', marginBottom: '24px',
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          boxShadow: '0 4px 20px rgba(52,71,103,0.2)',
        }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '14px', flexShrink: 0,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '20px',
          }}>
            <i className="fas fa-home" />
          </div>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <p style={{ color: '#fff', fontWeight: '700', fontSize: '17px', margin: 0 }}>{form.propertyName}</p>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', margin: 0, marginTop: '2px' }}>
              {[form.city, form.state, form.country].filter(Boolean).join(', ') || 'No location set'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {form.propertyType && (
              <span style={{
                background: typeColor + '33', color: '#fff',
                borderRadius: '20px', padding: '5px 16px', fontSize: '13px', fontWeight: '700',
                border: `1px solid ${typeColor}66`,
              }}>
                <i className="fas fa-building" style={{ marginRight: '6px' }} />{form.propertyType}
              </span>
            )}
            {formattedPrice && (
              <span style={{
                background: 'rgba(45,206,137,0.25)', color: '#fff',
                borderRadius: '20px', padding: '5px 16px', fontSize: '13px', fontWeight: '700',
                border: '1px solid rgba(45,206,137,0.4)',
              }}>
                <i className="fas fa-rupee-sign" style={{ marginRight: '6px' }} />{formattedPrice}
              </span>
            )}
            {form.bedrooms && (
              <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '20px', padding: '5px 16px', fontSize: '13px', fontWeight: '600' }}>
                <i className="fas fa-bed" style={{ marginRight: '6px' }} />{form.bedrooms} Bed &nbsp;·&nbsp; <i className="fas fa-bath" style={{ marginRight: '6px' }} />{form.bathrooms} Bath
              </span>
            )}
          </div>
        </div>
      )}

      <form onSubmit={e => e.preventDefault()}>
        <div style={{ background: '#fff', borderRadius: '20px', boxShadow: '0 4px 24px rgba(52,71,103,0.08)', overflow: 'hidden' }}>

          {/* Gradient strip */}
          <div style={{
            background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
            padding: '20px 28px', display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <i className="fas fa-home" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>Property Details</span>
          </div>

          <div style={{ padding: '32px 28px' }}>

            {/* Section 1 – Basic Info */}
            <div style={{ marginBottom: '28px' }}>
              <SecHead color="#5e72e4" icon="fa-home" title="Basic Information" />
              <div className="row g-3">
                <Field label="Property Name" name="propertyName" />
                <Field label="Owner Name" name="ownerName" />
                <div className="col-md-4">
                  <label style={lbl} htmlFor="propertyType">Property Type</label>
                  <select
                    id="propertyType" name="propertyType"
                    className="ep-select"
                    style={{ ...inp(!!formError.propertyType), appearance: 'none', cursor: 'pointer' }}
                    value={form.propertyType || ''} onChange={changeHandler}
                  >
                    <option value="">— Select Type —</option>
                    {['1BHK','2BHK','3BHK','4BHK','5BHK','Plot','Kothi'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  {formError.propertyType && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.propertyType}</p>}
                </div>
              </div>
            </div>

            {/* Inventory ownership */}
            <div style={{ marginBottom: '28px' }}>
              <SecHead color="#11cdef" icon="fa-handshake" title="Inventory ownership" />
              <div className="row g-3">
                <div className="col-md-4">
                  <label style={lbl} htmlFor="inventorySource">Source</label>
                  <select
                    id="inventorySource"
                    name="inventorySource"
                    className="ep-select"
                    style={{ ...inp(false), appearance: 'none', cursor: 'pointer' }}
                    value={form.inventorySource || 'Own'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        inventorySource: v,
                        ...(v === 'Own'
                          ? { builderId: '', channelCommissionPercent: '', channelAgreementNotes: '' }
                          : {}),
                      }));
                    }}
                  >
                    {INVENTORY_SOURCES.map((x) => (
                      <option key={x.id} value={x.id}>{x.label}</option>
                    ))}
                  </select>
                </div>
                {(form.inventorySource || 'Own') === 'ChannelPartner' && (
                  <>
                    <div className="col-md-4">
                      <label style={lbl} htmlFor="builderId">Linked builder</label>
                      <select
                        id="builderId"
                        name="builderId"
                        className="ep-select"
                        style={{ ...inp(false), appearance: 'none', cursor: 'pointer' }}
                        value={form.builderId ?? ''}
                        onChange={changeHandler}
                      >
                        <option value="">— Select —</option>
                        {builders.map((b) => (
                          <option key={b.id} value={String(b.id)}>{b.companyName || `Builder #${b.id}`}</option>
                        ))}
                      </select>
                    </div>
                    <Field label="Commission %" name="channelCommissionPercent" type="number" placeholder="Optional override %" />
                    <div className="col-md-12">
                      <label style={lbl} htmlFor="channelAgreementNotes">Agreement notes</label>
                      <textarea
                        id="channelAgreementNotes"
                        name="channelAgreementNotes"
                        rows={2}
                        className="ep-input"
                        style={{ ...inp(false), resize: 'vertical' }}
                        value={form.channelAgreementNotes || ''}
                        onChange={changeHandler}
                        placeholder="Commission terms, agreement reference…"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Section 2 – Location */}
            <div style={{ marginBottom: '28px' }}>
              <SecHead color="#fb6340" icon="fa-map-marker-alt" title="Location" />
              <div className="row g-3">
                <Field label="Street Address" name="streetAddress" />
                <Field label="City" name="city" />
                <Field label="State" name="state" />
                <Field label="Country" name="country" />
                <Field label="Postal Code" name="postalCode" type="number" />
              </div>
            </div>

            {/* Section 3 – Property Details */}
            <div style={{ marginBottom: '28px' }}>
              <SecHead color="#2dce89" icon="fa-chart-bar" title="Property Details" />
              <div className="row g-3">
                <Field label="Price (₹)" name="price" type="number" placeholder="e.g. 5000000" />
                <Field label="Size (sqft)" name="size" type="number" placeholder="e.g. 1200" />
                <Field label="Year Built" name="yearBuilt" type="number" placeholder="e.g. 2018" />
                <Field label="Bedrooms" name="bedrooms" type="number" placeholder="e.g. 3" />
                <Field label="Bathrooms" name="bathrooms" type="number" placeholder="e.g. 2" />
              </div>
            </div>

            {/* Section — Docs & Media */}
            <div style={{ marginBottom: '28px' }}>
              <SecHead color="#344767" icon="fa-folder-open" title="Docs & Media" />
              <p style={{ fontSize: '13px', color: '#8392ab', marginBottom: '16px', marginTop: '-8px' }}>
                Upload or link documents (deeds, NOC, layout) and property photos. Saved with this listing.
              </p>
              <PropertyDocsMediaUpload
                documents={documents}
                images={images}
                onDocumentsChange={setDocuments}
                onImagesChange={setImages}
                onToast={(opts) => toast.current?.show({ ...opts, life: 2800 })}
              />
            </div>

            {/* Section 4 – Description */}
            <div>
              <SecHead color="#11cdef" icon="fa-align-left" title="Description" />
              <div className="row g-3">
                <div className="col-12">
                  <label style={lbl} htmlFor="description">Property Description</label>
                  <textarea
                    id="description" name="description"
                    className="ep-input"
                    rows="4"
                    style={{ ...inp(!!formError.description), resize: 'vertical', minHeight: '100px' }}
                    placeholder="Describe the property — features, amenities, nearby landmarks…"
                    value={form.description || ''} onChange={changeHandler}
                  />
                  {formError.description && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.description}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '20px 28px', background: '#f8f9fe',
            borderTop: '1.5px solid #f0f2f5',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
          }}>
            <span style={{ fontSize: '13px', color: '#8392ab', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="fas fa-info-circle" style={{ color: '#5e72e4' }} />
              Changes will be saved immediately after clicking Update
            </span>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="ep-ghost"
                onClick={() => navigate(-1)}
                style={{
                  background: 'transparent', border: '1.5px solid #e9ecef',
                  borderRadius: '10px', padding: '10px 24px',
                  fontSize: '14px', fontWeight: '600', color: '#8392ab',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                <i className="fas fa-times" style={{ marginRight: '7px' }} />Cancel
              </button>
              <button
                type="button"
                className="ep-submit"
                onClick={updateContractor}
                style={{
                  background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
                  border: 'none', borderRadius: '10px', padding: '10px 32px',
                  fontSize: '14px', fontWeight: '700', color: '#fff',
                  cursor: 'pointer', boxShadow: '0 4px 15px rgba(52,71,103,0.3)',
                  display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s',
                }}
              >
                <i className="fas fa-save" />Update Property
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default EditProperty;
